import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

export interface PricingDecisionRow {
  id: string;
  scan_id: string | null;
  user_id: string | null;
  // Inputs
  haiku_output: Record<string, unknown> | null;
  triage_result: Record<string, unknown> | null;
  premier_store: Record<string, unknown> | null;
  comps_used: unknown[] | null;
  reference_sections: unknown[] | null;
  brand_tier: string | null;
  // Outputs
  sonnet_verdict: Record<string, unknown> | null;
  proto_judge_delta: number | null;
  verdict_tier: "Not Eligible" | "Verified" | "Likely Verified" | "Insufficient Evidence";
  band_low: number | null;
  band_high: number | null;
  band_suggested: number | null;
  confidence: number | null;
  // Shadow-mode comparison
  mode: "shadow" | "beta" | "production";
  legacy_verdict: Record<string, unknown> | null;
  legacy_band_low: number | null;
  legacy_band_high: number | null;
  legacy_band_suggested: number | null;
  legacy_verdict_tier: string | null;
  midpoint_delta_pct: number | null;
  verdict_tier_matches: boolean | null;
  // Audit
  created_at: string;
  workflow_duration_ms: number | null;
  partial_data: boolean;
}

export function usePricingDecisions() {
  return useQuery<PricingDecisionRow[]>({
    queryKey: ["pricing_decisions", "recent"],
    queryFn: async () => {
      // Phase 0 scaffold — pricing_decisions exists in DB (Phase 0 Task 1 migration)
      // but supabase types haven't been regenerated yet. Phase 1 will regenerate
      // types when the table starts being populated. Cast through unknown for now.
      const { data, error } = await (supabase
        .from("pricing_decisions" as never) as unknown as {
          select: (cols: string) => {
            order: (col: string, opts: { ascending: boolean }) => {
              limit: (n: number) => Promise<{ data: PricingDecisionRow[] | null; error: { message: string } | null }>;
            };
          };
        })
        .select("*")
        .order("created_at", { ascending: false })
        .limit(50);
      if (error) throw new Error(error.message);
      return (data ?? []) as PricingDecisionRow[];
    },
    staleTime: 30_000,
  });
}
